import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { FiChevronDown, FiChevronUp, FiX } from 'react-icons/fi';
import {
  clearScanDiagnostics,
  getScanDiagnostics,
  setScanDebugEnabled,
  subscribeScanDiagnostics,
  visualisePayload
} from '@/lib/scanning/scanDiagnostics';
import type { ScanDiagnosticEntry } from '@/lib/scanning/scanDiagnostics';
import './ScanDiagnostics.scss';

/**
 * Shows exactly what each scanner sent and what the app made of it. Turned on with
 * `?scandebug=1` and off again from its own close button.
 *
 * This exists because a handheld failing and a handheld succeeding-but-unusable look identical
 * from the operator's side — in both cases the screen simply does not move. Scanning the same
 * pack with the camera and then with the handheld puts the two payloads side by side, which is
 * usually the whole diagnosis.
 */
const CAPTURE_SELECTOR = '[data-hardware-scanner-capture]';

/**
 * Live state of the capture field. Worth showing because the commonest handheld failure is that
 * the field is absent or has lost focus — in which case a wedge delivering its scan as a string
 * has nowhere to put it, and the app sees nothing at all rather than something wrong.
 */
/** Enough to recognise an element on sight: `INPUT[type=text]#zone.foo`. */
const describeElement = (element: Element | null) => {
  if (!element) return 'nothing';
  const tag = element.tagName;
  const type = element.getAttribute('type');
  const id = element.id ? `#${element.id}` : '';
  const cls = element.className && typeof element.className === 'string'
    ? `.${element.className.trim().split(/\s+/).slice(0, 2).join('.')}`
    : '';
  return `${tag}${type ? `[type=${type}]` : ''}${id}${cls}`;
};

/**
 * Whatever currently holds focus, and whether the scanner will therefore ignore key events.
 *
 * The listener steps aside for any editable element other than its own capture field, so that a
 * barcode aimed at the manual-entry box lands there. The cost is that *anything* editable holding
 * focus silently stops scanning — and it is also the only thing that asks Android for a keyboard.
 * Naming it is the difference between fixing it and guessing.
 */
const useFocusState = () => {
  const [state, setState] = useState('checking…');

  useEffect(() => {
    const check = () => {
      const field = document.querySelector(CAPTURE_SELECTOR);
      const active = document.activeElement;
      const parts = [
        field
          ? active === field
            ? 'capture field focused'
            : `capture field present but NOT focused${
                field.getAttribute('inputmode') === 'none' ? ' (inputmode=none)' : ''
              }`
          : 'no capture field'
      ];
      parts.push(`focus: ${describeElement(active)}`);
      if (active && active !== field && active !== document.body) {
        const editable =
          (active as HTMLElement).isContentEditable ||
          ['INPUT', 'TEXTAREA', 'SELECT'].includes(active.tagName);
        if (editable) parts.push('⚠ editable — key events are being ignored');
      }
      setState(parts.join(' · '));
    };
    check();
    const timer = window.setInterval(check, 500);
    return () => window.clearInterval(timer);
  }, []);

  return state;
};

const ScanDiagnostics = () => {
  const [entries, setEntries] = useState<ScanDiagnosticEntry[]>(getScanDiagnostics());
  const [collapsed, setCollapsed] = useState(false);
  const focusState = useFocusState();

  useEffect(() => subscribeScanDiagnostics(setEntries), []);

  return createPortal(
    <div className={`scan-diagnostics${collapsed ? ' scan-diagnostics--collapsed' : ''}`}>
      <div className="scan-diagnostics__bar">
        <button
          type="button"
          className="scan-diagnostics__toggle"
          onClick={() => setCollapsed((value) => !value)}
        >
          {collapsed ? <FiChevronUp /> : <FiChevronDown />}
          <span>Scan debug · {entries.length}</span>
        </button>
        <button type="button" onClick={clearScanDiagnostics}>
          Clear
        </button>
        <button
          type="button"
          aria-label="Turn off scan debug"
          onClick={() => setScanDebugEnabled(false)}
        >
          <FiX />
        </button>
      </div>

      {!collapsed && (
        <div className="scan-diagnostics__list">
          <div className="scan-diagnostics__status">
            Scanner: <strong>{focusState}</strong>
          </div>
          {!entries.length && (
            <p className="scan-diagnostics__empty">
              Nothing captured yet. Pull the handheld trigger, or point the camera at a
              barcode. If pulling the trigger adds no row at all, the scan is not reaching the
              browser — check the DataWedge profile rather than the app.
            </p>
          )}
          {entries.map((entry) => (
            <div
              key={entry.id}
              className={`scan-diagnostics__row scan-diagnostics__row--${entry.source}${
                entry.rejected ? ' scan-diagnostics__row--rejected' : ''
              }`}
            >
              <div className="scan-diagnostics__meta">
                <strong>{entry.source}</strong>
                <span>{entry.raw.length} chars</span>
              </div>
              <code className="scan-diagnostics__raw">
                {visualisePayload(entry.raw) || '(empty)'}
              </code>
              <div className="scan-diagnostics__summary">{entry.summary}</div>
              {!!entry.rejected && (
                <div className="scan-diagnostics__note">Dropped: {entry.rejected}</div>
              )}
              {!!entry.note && <div className="scan-diagnostics__note">{entry.note}</div>}
            </div>
          ))}
        </div>
      )}
    </div>,
    document.body
  );
};

export default ScanDiagnostics;
